import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/stores/authStore'
import type { Project, ProjectInsert, ProjectUpdate } from '@/lib/database.types'

export interface ProjectWithDesignCount extends Project {
  design_count: number
}

export interface ProjectsData {
  projects: ProjectWithDesignCount[]
  unassignedCount: number
}

export function useProjects() {
  const { user } = useAuthStore()
  const queryClient = useQueryClient()

  const {
    data: projectsData = { projects: [], unassignedCount: 0 },
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['projects', user?.id],
    queryFn: async (): Promise<ProjectsData> => {
      if (!user?.id) return { projects: [], unassignedCount: 0 }
      
      // Fetch projects
      const { data: projectsList, error: projectsError } = await supabase
        .from('projects')
        .select('*')
        .eq('user_id', user.id)
        .neq('status', 'deleted')
        .order('updated_at', { ascending: false })
      
      if (projectsError) throw projectsError
      
      // Use RPC function to get design counts (bypasses RLS)
      const { data: designCounts, error: countsError } = await (supabase as any)
        .rpc('get_design_counts_for_user', { p_user_id: user.id })
      
      if (countsError) {
        console.error('[useProjects] Error fetching design counts:', countsError)
      }
      
      // Build count map from RPC result
      const countMap: Record<string, number> = {}
      let unassignedCount = 0
      
      ;(designCounts || []).forEach((row: { project_id: string | null, design_count: number }) => {
        if (row.project_id) {
          countMap[row.project_id] = Number(row.design_count)
        } else {
          unassignedCount = Number(row.design_count)
        }
      })
      
      // Merge counts into projects
      console.log('[useProjects] countMap:', JSON.stringify(countMap))
      console.log('[useProjects] projectsList:', projectsList?.map(p => p.id))
      const projects = (projectsList || []).map(project => {
        const count = countMap[project.id]
        console.log('[useProjects] project.id:', project.id, 'countMap[id]:', count, 'type:', typeof count)
        return {
          ...project,
          design_count: count !== undefined ? count : 888
        }
      })
      
      console.log('[useProjects] Final projects:', projects)
      return { projects, unassignedCount }
    },
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 2,
  })
  
  const { projects, unassignedCount } = projectsData

  const createProjectMutation = useMutation({
    mutationFn: async (project: Omit<ProjectInsert, 'user_id'>) => {
      if (!user?.id || !user?.email) throw new Error('Not authenticated')
      
      // Ensure profile exists (handles case where trigger didn't run)
      const { data: existingProfile } = await supabase
        .from('profiles')
        .select('id')
        .eq('id', user.id)
        .single()
      
      if (!existingProfile) {
        const { error: profileError } = await supabase
          .from('profiles')
          .insert({
            id: user.id,
            email: user.email,
            full_name: user.name,
          })
        
        if (profileError && profileError.code !== '23505') {
          // 23505 = unique violation (profile already exists)
          throw new Error('Failed to create user profile')
        }
      }
      
      const { data, error } = await supabase
        .from('projects')
        .insert({ ...project, user_id: user.id })
        .select()
        .single()
      
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects', user?.id] })
    },
  })

  const updateProjectMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: ProjectUpdate }) => {
      const { data, error } = await supabase
        .from('projects')
        .update(updates)
        .eq('id', id)
        .select()
        .single()
      
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects', user?.id] })
    },
  })

  const deleteProjectMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('projects')
        .update({ status: 'deleted' })
        .eq('id', id)
      
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects', user?.id] })
    },
  })

  return {
    projects,
    unassignedCount,
    isLoading,
    error,
    refetch,
    createProject: createProjectMutation.mutateAsync,
    updateProject: updateProjectMutation.mutateAsync,
    deleteProject: deleteProjectMutation.mutateAsync,
    isCreating: createProjectMutation.isPending,
    isUpdating: updateProjectMutation.isPending,
    isDeleting: deleteProjectMutation.isPending,
  }
}

export function useProject(projectId: string | undefined) {
  const { user } = useAuthStore()

  return useQuery({
    queryKey: ['project', projectId],
    queryFn: async (): Promise<Project | null> => {
      if (!projectId) return null
      
      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .eq('id', projectId)
        .single()
      
      if (error) {
        if (error.code === 'PGRST116') return null
        throw error
      }
      return data
    },
    enabled: !!projectId && !!user?.id,
  })
}
